import {
  Flex,
  Box,
  DrawerContent,
  DrawerCloseButton,
  InputGroup,
  InputRightElement,
  FormLabel,
  useDisclosure,
  Text,
  Input,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { CirclePicker, Color, ColorResult } from 'react-color';
import { useTranslation } from 'react-i18next';
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi';

import Button from 'components/Button';
import { ControllableDrawer } from 'components/Drawer';
import { FormikInput, FormikRadio, FormikTextArea, FormikSwitch } from 'components/Formik';
import AssignEmployeesAndGroupsField from 'containers/JobsTasks/RightDrawer/AssignEmployeesAndGroupsField';

import { initialCategoryFormData } from '../schema';

const General = () => {
  const { t } = useTranslation(['common']);
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  const { values, setFieldValue } = useFormikContext<typeof initialCategoryFormData>();

  const handleColorChange = (color: ColorResult) => {
    setFieldValue('color', color);
  };

  return (
    <div>
      {' '}
      <FormikInput type="text" name="name" label="Category Name" formControlProps={{ isRequired: true }} />
      <Flex
        as="fieldset"
        disabled={values.id ? true : false}
        pointerEvents={values.id ? 'none' : 'auto'}
        opacity={values.id ? 0.6 : 1}
        justifyContent="space-between"
        mb="4"
      >
        <FormikRadio
          label="Type"
          name="is_paid"
          stackProps={{ direction: 'row', spacing: '4' }}
          radioProps={{ colorScheme: 'secondary' }}
          options={[
            { label: 'Paid', value: 'true' },
            { label: 'Unpaid', value: 'false' },
          ]}
        />

        <FormikRadio
          label="Duration Unit"
          name="in_hours"
          stackProps={{ direction: 'row', spacing: '4' }}
          radioProps={{ colorScheme: 'secondary' }}
          options={[
            { label: 'Days', value: 'false' },
            { label: 'Hours', value: 'true' },
          ]}
        />
      </Flex>
      <Box mb="5">
        <FormLabel htmlFor="active" color="gray.500" fontSize="12px" fontWeight="600" mb="3" textTransform="uppercase">
          Calendar Color
        </FormLabel>

        <CirclePicker
          width="100%"
          circleSize={20}
          circleSpacing={10}
          color={values.color as Color | undefined}
          styles={{ default: { card: { border: 'none', boxShadow: 'none' } } }}
          onChange={handleColorChange}
          // colors={colors}
        />
      </Box>
      <Box mb="5">
        <ControllableDrawer onClose={onDrawerClose} isOpen={isDrawerOpen} size="lg">
          <DrawerContent>
            <DrawerCloseButton />
            <AssignEmployeesAndGroupsField />
          </DrawerContent>
        </ControllableDrawer>
        <Text color="gray.500" fontSize="12px" fontWeight="600" mb="1" textTransform="uppercase">
          Applicable For
        </Text>

        <InputGroup size="lg">
          <Input
            fontSize="sm"
            variant="filled"
            type="text"
            border="1px"
            borderColor="inherit"
            onChange={() => null}
            value={`${values.users.length || t('common:all')} ${
              values.users.length === 1 ? t('common:user') : t('common:users')
            }`}
          />
          <InputRightElement
            width="auto"
            mr="2"
            children={
              <Button size="sm" variant="link" onClick={onDrawerOpen}>
                {values.users.length ? t('common:edit') : t('common:add')}
              </Button>
            }
          />
        </InputGroup>
      </Box>
      <FormikTextArea name="description" label={t('common:description')} />
      <Box mb="4">
        <FormLabel htmlFor="active" color="gray.500" fontSize="12px" fontWeight="600" mb="2" textTransform="uppercase">
          <Flex alignItems="center">
            Leave Duration
            <Tooltip
              label={`Sets the minimum and maximum number of leave ${
                values.in_hours === 'false' ? 'days' : 'hours'
              } an employee can request at once.`}
            >
              <Box ml="1">
                <Icon as={HiOutlineQuestionMarkCircle} height="18px" width="18px" color="gray.400" />
              </Box>
            </Tooltip>
          </Flex>
        </FormLabel>

        <Flex alignItems="flex-start" gridGap="2" mb="7">
          <Text as="span" pt="3">
            From
          </Text>
          <FormikInput
            name="min_request_duration"
            formControlProps={{ minWidth: '64px', width: '64px', mb: 0 }}
            inputProps={{ placeholder: 'min' }}
          />
          <Text as="span" pt="3">
            to
          </Text>
          <FormikInput
            name="max_request_duration"
            formControlProps={{ minWidth: '64px', width: '64px', mb: 0 }}
            inputProps={{ placeholder: 'max' }}
          />
          <Text as="span" pt="3">
            {values.in_hours === 'false' ? 'days' : 'hours'} per request
          </Text>
        </Flex>
      </Box>
      <Box mb="4">
        <FormLabel htmlFor="active" color="gray.500" fontSize="12px" fontWeight="600" mb="2" textTransform="uppercase">
          Activate Category?
        </FormLabel>
        <FormikSwitch name="active" />
      </Box>
    </div>
  );
};

export default General;
